import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Cliente, Documento, Expediente, MovimientoEconomico, TipoCliente } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Campo, Input, Select } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { Tarjeta, TarjetaIndicador } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { FormularioExpediente } from "../expedientes/FormularioExpediente";

// Forma exacta de GET /clientes/:id/resumen (ver functions/_app/routes/clientes.ts).
// Sólo agregados que no podrían obtenerse sin N+1 — las listas completas
// (expedientes/documentos/movimientos) NO viven acá, se piden por pestaña
// a los endpoints ya existentes con ?cliente_id=.
interface ResumenCliente {
  cliente: Cliente;
  expedientes: { activos: number; archivados: number; alcance: "todos" | "asignados" | "ninguno" };
  economico?: {
    fondosEnCustodiaPorMoneda: { moneda: string; monto: number }[];
    honorariosPorMoneda: { moneda: string; vigente: number; cobrado: number; saldoPendiente: number }[];
  };
}

const TIPOS_CLIENTE: TipoCliente[] = ["Persona Física", "Persona Jurídica"];

const PESTANAS = [
  { clave: "expedientes", etiqueta: "Expedientes" },
  { clave: "economico", etiqueta: "Económico" },
  { clave: "documentos", etiqueta: "Documentos" },
] as const;
type ClavePestana = (typeof PESTANAS)[number]["clave"];

function formatearImporte(monto: number, moneda: string): string {
  return `$${monto.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda}`;
}

export function ClienteFichaPage() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const { permisos } = useAuth();
  const veExpedientes = permisos.has("expedientes.ver_todos") || permisos.has("expedientes.ver_asignados");
  const veEconomia = permisos.has("economia.ver");
  const veDocumentos = permisos.has("documentos.ver");
  const veCrearExpediente = permisos.has("expedientes.crear");
  const veEditarCliente = permisos.has("clientes.editar");

  const [resumen, setResumen] = useState<ResumenCliente | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pestana, setPestana] = useState<ClavePestana | null>(null);

  const [modalEditar, setModalEditar] = useState(false);
  const [valoresEdicion, setValoresEdicion] = useState({
    tipo_cliente: "Persona Física" as TipoCliente,
    nombre_completo: "",
    identificacion: "",
    email: "",
    telefono: "",
    domicilio: "",
    localidad: "",
  });
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  const [modalExpediente, setModalExpediente] = useState(false);
  const [guardandoExpediente, setGuardandoExpediente] = useState(false);

  function cargarResumen() {
    if (!id) return;
    setError(null);
    api
      .get<ResumenCliente>(`/clientes/${id}/resumen`)
      .then(setResumen)
      .catch((err) => setError(err instanceof ErrorApi ? err.message : "No se pudo cargar el cliente."));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargarResumen, [id]);

  const pestanasVisibles = PESTANAS.filter((p) => p.clave !== "expedientes" || veExpedientes)
    .filter((p) => p.clave !== "economico" || veEconomia)
    .filter((p) => p.clave !== "documentos" || veDocumentos);

  // Primera pestaña visible por defecto, una sola vez, cuando el resumen ya
  // permitió calcular pestanasVisibles — nunca se sobreescribe una elección
  // manual posterior del usuario.
  useEffect(() => {
    if (resumen && pestana === null && pestanasVisibles.length > 0) {
      setPestana(pestanasVisibles[0].clave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumen]);

  function abrirEditar(cliente: Cliente) {
    setValoresEdicion({
      tipo_cliente: cliente.tipo_cliente,
      nombre_completo: cliente.nombre_completo,
      identificacion: cliente.identificacion ?? "",
      email: cliente.email ?? "",
      telefono: cliente.telefono ?? "",
      domicilio: cliente.domicilio ?? "",
      localidad: cliente.localidad ?? "",
    });
    setModalEditar(true);
  }

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setGuardandoEdicion(true);
    try {
      await api.put(`/clientes/${id}`, valoresEdicion);
      mostrar("Cliente actualizado.", "exito");
      setModalEditar(false);
      cargarResumen();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el cliente.", "error");
    } finally {
      setGuardandoEdicion(false);
    }
  }

  async function crearExpediente(valores: Record<string, unknown>) {
    setGuardandoExpediente(true);
    try {
      const datos = await api.post<{ expediente: Expediente }>("/expedientes", valores);
      mostrar("Expediente creado correctamente.", "exito");
      setModalExpediente(false);
      navegar(`/expedientes/${datos.expediente.id}`, { state: { from: `/clientes/${id}` } });
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo crear el expediente.", "error");
    } finally {
      setGuardandoExpediente(false);
    }
  }

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!resumen) return <Cargando />;

  const { cliente } = resumen;

  return (
    <div>
      <EncabezadoPagina
        titulo={cliente.nombre_completo}
        descripcion={`${cliente.tipo_cliente} · ${cliente.activo ? "Activo" : "Archivado"}`}
        volver={{ rutaPadre: "/clientes" }}
        acciones={
          <>
            {veEditarCliente && (
              <Boton variante="secundario" onClick={() => abrirEditar(cliente)}>
                Editar cliente
              </Boton>
            )}
            {veCrearExpediente && <Boton onClick={() => setModalExpediente(true)}>+ Nuevo expediente</Boton>}
          </>
        }
      />

      {/* Cabecera: sólo datos reales del cliente, sin inventar un "estado" distinto de Activo/Archivado. */}
      <Tarjeta className="mb-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
        <Dato etiqueta="Identificación (DNI/CUIT)" valor={cliente.identificacion ?? "—"} />
        <Dato etiqueta="Email" valor={cliente.email ?? "—"} />
        <Dato etiqueta="Teléfono" valor={cliente.telefono ?? "—"} />
        <Dato etiqueta="Domicilio" valor={cliente.domicilio ?? "—"} />
        <Dato etiqueta="Localidad" valor={cliente.localidad ?? "—"} />
      </Tarjeta>

      {/* Resumen compacto — Expedientes con copy honesto según scope real;
          Económico sólo si economia.ver, con los 3 conceptos siempre
          separados (nunca un "saldo del cliente" único). */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {veExpedientes && (
          <>
            <TarjetaIndicador
              etiqueta={
                resumen.expedientes.alcance === "asignados" ? "Expedientes activos (asignados a vos)" : "Expedientes activos"
              }
              valor={resumen.expedientes.activos}
            />
            <TarjetaIndicador
              etiqueta={
                resumen.expedientes.alcance === "asignados"
                  ? "Expedientes inactivos (asignados a vos)"
                  : "Expedientes inactivos"
              }
              valor={resumen.expedientes.archivados}
            />
          </>
        )}
        {resumen.economico?.fondosEnCustodiaPorMoneda.map((f) => (
          <TarjetaIndicador key={`custodia-${f.moneda}`} etiqueta={`Fondos en custodia (${f.moneda})`} valor={formatearImporte(f.monto, f.moneda)} />
        ))}
        {resumen.economico?.honorariosPorMoneda.map((h) => (
          <TarjetaIndicador
            key={`pendiente-${h.moneda}`}
            etiqueta={`Honorarios pendientes (${h.moneda})`}
            valor={formatearImporte(h.saldoPendiente, h.moneda)}
            tono={h.saldoPendiente > 0 ? "alerta" : "neutro"}
          />
        ))}
      </div>

      {pestanasVisibles.length === 0 ? (
        <EstadoVacio
          titulo="Sin secciones disponibles"
          descripcion="Tu usuario no tiene permisos para ver Expedientes, Económico ni Documentos de este cliente."
        />
      ) : (
        <>
          <div className="mb-6 flex gap-1 border-b border-borde">
            {pestanasVisibles.map((p) => (
              <button
                key={p.clave}
                onClick={() => setPestana(p.clave)}
                className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2 ${
                  pestana === p.clave
                    ? "border-principal text-principal"
                    : "border-transparent text-texto-secundario hover:text-texto"
                }`}
              >
                {p.etiqueta}
              </button>
            ))}
          </div>

          {pestana === "expedientes" && <PestanaExpedientesCliente clienteId={cliente.id} />}
          {pestana === "economico" && <PestanaEconomicoCliente clienteId={cliente.id} resumen={resumen.economico} />}
          {pestana === "documentos" && <PestanaDocumentosCliente clienteId={cliente.id} />}
        </>
      )}

      <Modal titulo="Editar cliente" abierto={modalEditar} onCerrar={() => setModalEditar(false)}>
        <form onSubmit={guardarEdicion} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Tipo" obligatorio>
              <Select
                value={valoresEdicion.tipo_cliente}
                onChange={(e) => setValoresEdicion((v) => ({ ...v, tipo_cliente: e.target.value as TipoCliente }))}
              >
                {TIPOS_CLIENTE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Nombre completo / Razón social" obligatorio>
              <Input
                required
                value={valoresEdicion.nombre_completo}
                onChange={(e) => setValoresEdicion((v) => ({ ...v, nombre_completo: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Identificación (DNI/CUIT)">
              <Input
                value={valoresEdicion.identificacion}
                onChange={(e) => setValoresEdicion((v) => ({ ...v, identificacion: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Email">
              <Input
                type="email"
                value={valoresEdicion.email}
                onChange={(e) => setValoresEdicion((v) => ({ ...v, email: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Teléfono">
              <Input
                value={valoresEdicion.telefono}
                onChange={(e) => setValoresEdicion((v) => ({ ...v, telefono: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Localidad">
              <Input
                value={valoresEdicion.localidad}
                onChange={(e) => setValoresEdicion((v) => ({ ...v, localidad: e.target.value }))}
              />
            </Campo>
          </div>
          <Campo etiqueta="Domicilio">
            <Input
              value={valoresEdicion.domicilio}
              onChange={(e) => setValoresEdicion((v) => ({ ...v, domicilio: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton type="button" variante="secundario" onClick={() => setModalEditar(false)} disabled={guardandoEdicion}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoEdicion}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      {/* Un único cliente en la lista (ya elegido por venir de su propia
          ficha): FormularioExpediente ya soporta precargar cliente_id vía
          valoresIniciales sin ningún cambio — no hizo falta tocar ese
          formulario para este bloque. */}
      <FormularioExpediente
        abierto={modalExpediente}
        clientes={[cliente]}
        guardando={guardandoExpediente}
        valoresIniciales={{ cliente_id: cliente.id }}
        onCerrar={() => setModalExpediente(false)}
        onGuardar={crearExpediente}
      />
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">{etiqueta}</p>
      <p className="mt-0.5 text-sm text-texto">{valor}</p>
    </div>
  );
}

// ── Pestaña Expedientes ──────────────────────────────────────────────────
// Carga perezosa: sólo pide GET /expedientes?cliente_id= al montar (cuando
// el usuario abre esta pestaña), reutilizando el payload real del listado
// global — nunca reconstruye ExpedientesListaPage.tsx.
function PestanaExpedientesCliente({ clienteId }: { clienteId: string }) {
  const navegar = useNavigate();
  const [expedientes, setExpedientes] = useState<Expediente[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setExpedientes(null);
    setError(null);
    api
      .get<{ items: Expediente[] }>(`/expedientes?cliente_id=${encodeURIComponent(clienteId)}`)
      .then((d) => setExpedientes(d.items))
      .catch((err) => setError(err instanceof ErrorApi ? err.message : "No se pudieron cargar los expedientes."));
  }, [clienteId]);

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!expedientes) return <Cargando />;

  const columnas: ColumnaTabla<Expediente>[] = [
    { encabezado: "Número", render: (e) => <span className="font-medium text-texto">{e.numero_expediente}</span> },
    { encabezado: "Materia", render: (e) => e.materia_nombre ?? "—" },
    { encabezado: "Estado", render: (e) => <Insignia texto={e.estado} tipo="estado" /> },
    { encabezado: "Prioridad", render: (e) => <Insignia texto={e.prioridad} tipo="prioridad" /> },
  ];

  return (
    <Tabla
      columnas={columnas}
      filas={expedientes}
      claveFila={(e) => e.id}
      onFilaClick={(e) => navegar(`/expedientes/${e.id}`, { state: { from: `/clientes/${clienteId}` } })}
      tituloVacio="Sin expedientes"
      descripcionVacio="Este cliente todavía no tiene expedientes visibles para vos."
    />
  );
}

// ── Pestaña Económico ────────────────────────────────────────────────────
// Carga perezosa de movimientos (GET /economia?cliente_id=, mismo endpoint
// que EconomiaPage.tsx). El resumen de fondos/honorarios viaja ya calculado
// desde /clientes/:id/resumen — acá sólo se agrega el detalle de
// movimientos, sin recalcular nada.
function PestanaEconomicoCliente({
  clienteId,
  resumen,
}: {
  clienteId: string;
  resumen: ResumenCliente["economico"];
}) {
  const [movimientos, setMovimientos] = useState<MovimientoEconomico[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMovimientos(null);
    setError(null);
    api
      .get<{ items: MovimientoEconomico[] }>(`/economia?cliente_id=${encodeURIComponent(clienteId)}`)
      .then((d) => setMovimientos(d.items))
      .catch((err) => setError(err instanceof ErrorApi ? err.message : "No se pudieron cargar los movimientos económicos."));
  }, [clienteId]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-texto">Fondos del cliente</h3>
        {resumen && resumen.fondosEnCustodiaPorMoneda.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {resumen.fondosEnCustodiaPorMoneda.map((f) => (
              <Tarjeta key={f.moneda} tamano="compacta">
                <p className="text-xs text-texto-secundario">Custodia ({f.moneda})</p>
                <p className="mt-1 text-lg font-semibold text-texto">{formatearImporte(f.monto, f.moneda)}</p>
              </Tarjeta>
            ))}
          </div>
        ) : (
          <p className="text-sm text-texto-secundario">Sin fondos en custodia registrados.</p>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-texto">Honorarios</h3>
        {resumen && resumen.honorariosPorMoneda.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {resumen.honorariosPorMoneda.map((h) => (
              <Tarjeta key={h.moneda} tamano="compacta">
                <p className="text-xs text-texto-secundario">{h.moneda}</p>
                <p className="mt-1 text-sm text-texto">Vigente: {formatearImporte(h.vigente, h.moneda)}</p>
                <p className="text-sm text-texto">Cobrado: {formatearImporte(h.cobrado, h.moneda)}</p>
                <p className="text-sm font-medium text-texto">Saldo pendiente: {formatearImporte(h.saldoPendiente, h.moneda)}</p>
              </Tarjeta>
            ))}
          </div>
        ) : (
          <p className="text-sm text-texto-secundario">Sin honorarios registrados en los expedientes de este cliente.</p>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-texto">Movimientos</h3>
        {error ? (
          <EstadoVacio titulo="Error" descripcion={error} />
        ) : !movimientos ? (
          <Cargando />
        ) : (
          <Tabla
            columnas={[
              { encabezado: "Fecha", render: (m) => m.fecha },
              { encabezado: "Tipo", render: (m) => m.tipo },
              { encabezado: "Concepto", render: (m) => m.concepto },
              {
                encabezado: "Importe",
                render: (m) => formatearImporte(m.importe, m.moneda),
              },
            ]}
            filas={movimientos}
            claveFila={(m) => m.id}
            tituloVacio="Sin movimientos"
            descripcionVacio="Este cliente todavía no tiene movimientos económicos registrados."
          />
        )}
      </div>
    </div>
  );
}

// ── Pestaña Documentos ───────────────────────────────────────────────────
function PestanaDocumentosCliente({ clienteId }: { clienteId: string }) {
  const [documentos, setDocumentos] = useState<(Documento & { numero_expediente?: string | null })[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDocumentos(null);
    setError(null);
    api
      .get<{ items: (Documento & { numero_expediente?: string | null })[] }>(
        `/documentos?cliente_id=${encodeURIComponent(clienteId)}`,
      )
      .then((d) => setDocumentos(d.items))
      .catch((err) => setError(err instanceof ErrorApi ? err.message : "No se pudieron cargar los documentos."));
  }, [clienteId]);

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!documentos) return <Cargando />;

  return (
    <Tabla
      columnas={[
        { encabezado: "Título", render: (d) => d.titulo },
        { encabezado: "Categoría", render: (d) => d.categoria ?? "—" },
        { encabezado: "Expediente", render: (d) => d.numero_expediente ?? "—" },
        { encabezado: "Fecha", render: (d) => d.creado_en.substring(0, 10) },
        {
          encabezado: "Acciones",
          render: (d) => (
            <a
              href={`/api/documentos/${d.id}/descargar`}
              className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Descargar
            </a>
          ),
        },
      ]}
      filas={documentos}
      claveFila={(d) => d.id}
      tituloVacio="Sin documentos"
      descripcionVacio="Este cliente todavía no tiene documentos registrados."
    />
  );
}
